import { useMemo, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getWeekPlan,
  getWeekReview,
  type WeekReview,
  moveBlock,
  scheduleTask,
  updateTask,
  type WeekChip,
  type WeekDay,
  type WeekPlan,
} from '../api';
import { useAccountDay } from '../accountDay';
import { useMedia, PANES_STACK } from '../media';
import { shiftDay, startOfWeek } from '../timezones';
import { minuteLabel, spanLabel } from '../durations';
import { SNAP_MINUTES, START_HOUR, END_HOUR } from '../timelineLayout';
import { PeriodNav } from './PeriodNav';
import { QueryState } from './QueryState';
import { habitFigure, reviewAsOf } from '../habitFigure';

/**
 * Seven days side by side, with the pile you fill them from on the left.
 *
 * ## What a real week actually holds
 *
 * Measured on the local instance on 2026-09-10, across all 22 accounts: **no** open task carried
 * a plan date inside the current week, and **no** calendar event or block fell in it — 64 events
 * exist and every one of them is between 2026-08-16 and 2026-09-06. `manager@persona.test`, the
 * persona this screen is for, holds 61 open rows of which 24 are dated: 21 three weeks behind and
 * three two weeks ahead. Habits are barely fuller — four entries this week across every account,
 * and the manager has no habits at all.
 *
 * So the ordinary state of this screen is a full pile beside seven empty columns, and that is the
 * state it is designed for rather than the one it falls back to. The pile is the screen; the
 * columns are targets. What follows from that:
 *
 *  - the pile rail is not collapsible and does not wait for the columns to have content;
 *  - an entirely empty grid says so once, across the middle, instead of leaving somebody looking
 *    at an unexplained sheet of hour lines;
 *  - the habits row is absent, not empty, when the account keeps no habits — a labelled band of
 *    seven blank cells is a worse answer than no band.
 *
 * ## Two precisions
 *
 * Dropping a chip on a column's header or its dated strip **dates** it (`updateTask`). Dropping
 * it on an hour **places** it (`scheduleTask`, which writes the block *and* the date). Those are
 * different promises, so they look different: a dated chip is dashed and sits at the top of the
 * column, a placed one is a solid block on the grid at its hour.
 *
 * ## What is not drawn, and why
 *
 * Each column carries a whole `budget`, the same object the day screen's bar reads. The header
 * prints `freeMinutes` and the column's own `dated.minutes`. It does **not** print
 * `shortlistedMinutes`: that counts the shortlist *for that date*, which is close to the same
 * offer in every column — 37 of the manager's 61 open rows carry no plan date, so they are a
 * candidate on all seven — and seven copies of one number read as seven separate loads. `dated`
 * is the figure that differs by column, because it is a different question.
 */
export function Week({
  onOpenTask,
  onOpenDay,
}: {
  onOpenTask: (id: string) => void;
  /** Take the phone to the day screen for a date — the one place an hour can be picked there. */
  onOpenDay: (date: string) => void;
}) {
  const queryClient = useQueryClient();
  const { today } = useAccountDay();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(today));
  /* Below 60rem the seven columns become seven cards. `PANES_STACK` rather than `PHONE` because
     the thing that stops fitting is the same thing that stops fitting on Today: a row of columns
     beside a rail. See `media.ts`. */
  const stacked = useMedia(PANES_STACK);

  const plan = useQuery({
    queryKey: ['week', weekStart],
    queryFn: () => getWeekPlan(weekStart),
  });
  /*
    The habits row is `getWeekReview`, unchanged and already built.

    Not seven `get_day_plan` habit reads: a habit's week is a per-user figure, so asking it once
    is both cheaper and the only way the row's running total can agree with the Reflection screen
    that shows the same week.
  */
  const review = useQuery({
    queryKey: ['review', 'week', weekStart],
    queryFn: () => getWeekReview(weekStart),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['week'] });
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['day'] });
  };

  /** Drop on a column, not on an hour: the day is chosen, the hour is not. */
  const date = useMutation({
    mutationFn: (args: { taskId: string; day: string }) =>
      updateTask(args.taskId, { scheduledDate: args.day }),
    onSuccess: invalidate,
  });

  /** Drop on an hour. `scheduleTask` writes the block and moves the plan date with it. */
  const place = useMutation({
    mutationFn: (args: { taskId: string; startsAt: string; durationMinutes?: number }) =>
      scheduleTask(args.taskId, args.startsAt, args.durationMinutes),
    onSuccess: invalidate,
  });

  /** An existing block dragged to another column, or another hour of its own. */
  const move = useMutation({
    mutationFn: (args: { eventId: string; startsAt: string }) =>
      moveBlock(args.eventId, { startsAt: args.startsAt }),
    onSuccess: invalidate,
  });

  /** Which column the pointer is over, and at what minute — one preview, not seven. */
  const [preview, setPreview] = useState<{ date: string; minutes: number } | null>(null);

  /*
    The hours drawn, over the whole week rather than per column.

    Seven columns that each chose their own window would not line up: an hour row at the same y
    would be 09:00 in one column and 11:00 in the next, and the grid would stop being readable as
    a week. So the window is the union — the account's preference, widened by everything scheduled
    anywhere in the week, which is `visibleWindow`'s rule applied once across seven days.

    `dayStart` is read off each column rather than recomputed, and the arithmetic below is on
    milliseconds. Nothing here calls `localDay` or builds an `Intl.DateTimeFormat`: those cost
    ~81µs each and this draws seven columns of up to eighteen hour rows.
  */
  const window = useMemo(() => {
    let startHour = START_HOUR;
    let endHour = END_HOUR;
    for (const day of plan.data?.days ?? []) {
      const dayStart = new Date(day.dayStart).getTime();
      for (const event of day.events) {
        const from = (new Date(event.startsAt).getTime() - dayStart) / 60_000;
        const to = (new Date(event.endsAt).getTime() - dayStart) / 60_000;
        startHour = Math.min(startHour, Math.floor(from / 60));
        endHour = Math.max(endHour, Math.ceil(to / 60));
      }
    }
    return {
      startHour: Math.max(0, Math.min(startHour, 23)),
      /* Capped at the longest column rather than at 24: a 25-hour day has an hour past midnight
         that is still that day, and clipping it would put a block nowhere. */
      endHour: Math.min(
        Math.max(...(plan.data?.days ?? []).map((d) => d.hours), 24),
        Math.max(endHour, startHour + 1),
      ),
    };
  }, [plan.data]);

  const hours = Array.from(
    { length: Math.max(1, window.endHour - window.startHour) },
    (_, i) => window.startHour + i,
  );

  /** A drop position inside a column, snapped, as an instant on that column's day. */
  const instantIn = (day: WeekDay, offsetY: number): string => {
    const raw = window.startHour * 60 + (offsetY / WEEK_PX_PER_HOUR) * 60;
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
    /* Clamped to the column's own length, which is 23 or 25 hours on the days the clocks move.
       Without this a pointer at the bottom of a 23-hour Sunday would place a block on Monday. */
    const minutes = Math.min(Math.max(snapped, 0), day.hours * 60 - SNAP_MINUTES);
    return new Date(new Date(day.dayStart).getTime() + minutes * 60_000).toISOString();
  };

  const minutesIn = (day: WeekDay, offsetY: number): number => {
    const raw = window.startHour * 60 + (offsetY / WEEK_PX_PER_HOUR) * 60;
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.min(Math.max(snapped, 0), day.hours * 60 - SNAP_MINUTES);
  };

  /*
    Both ids travel in `dataTransfer` rather than in React state.

    The same reason `Today.tsx` gives: state set during `dragstart` is only visible to the drop
    handler if a re-render happened in between, and nothing guarantees one. This is what the drag
    API provides for exactly this.
  */
  const startDraggingChip = (e: DragEvent, chip: WeekChip) => {
    e.dataTransfer.setData('planner/task', chip.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const dropOnGrid = (e: DragEvent<HTMLDivElement>, day: WeekDay) => {
    e.preventDefault();
    setPreview(null);
    const bounds = e.currentTarget.getBoundingClientRect();
    const startsAt = instantIn(day, e.clientY - bounds.top);
    const eventId = e.dataTransfer.getData('planner/event');
    const taskId = e.dataTransfer.getData('planner/task');
    if (eventId) move.mutate({ eventId, startsAt });
    else if (taskId) {
      const chip = chipFor(plan.data, taskId);
      place.mutate({
        taskId,
        startsAt,
        /* The estimate is the block's length, or the same half hour a click on Today's grid
           means when nobody said. Sent explicitly rather than left to the server's default so the
           two surfaces cannot drift apart about what an unestimated task takes. */
        durationMinutes: chip?.estimateMinutes ?? DEFAULT_BLOCK_MINUTES,
      });
    }
  };

  const dropOnColumn = (e: DragEvent, day: WeekDay) => {
    e.preventDefault();
    setPreview(null);
    const taskId = e.dataTransfer.getData('planner/task');
    /* A block dragged onto a header is a mistake, not a date: it is already placed, and turning
       a placed thing into a merely dated one would silently throw away the hour. Ignored. */
    if (taskId) date.mutate({ taskId, day: day.date });
  };

  if (plan.isPending || plan.isError) {
    return (
      <div className="week-screen">
        <QueryState query={plan} what="this week" />
      </div>
    );
  }

  const weekPlan = plan.data;
  const anythingPlanned = weekPlan.days.some((d) => d.events.length > 0 || d.tasks.length > 0);

  const nav = (
    <div className="capture today-controls">
      <PeriodNav
        label="week"
        onPrevious={() => setWeekStart(shiftDay(weekStart, -7))}
        onNext={() => setWeekStart(shiftDay(weekStart, 7))}
      >
        {weekStart === startOfWeek(today) ? (
          <span className="week-range">
            {dayLabel(weekPlan.weekStart)} – {dayLabel(weekPlan.weekEnd)}
          </span>
        ) : (
          /* A way back, only when you are not already here — the same rule `PeriodNav` applies to
             its own Today button, which this slot displaces. */
          <button
            type="button"
            className="ghost week-range"
            onClick={() => setWeekStart(startOfWeek(today))}
          >
            {dayLabel(weekPlan.weekStart)} – {dayLabel(weekPlan.weekEnd)} · this week
          </button>
        )}
      </PeriodNav>
    </div>
  );

  const pile = (
    <aside className="week-pile" data-testid="week-pile">
      <div className="week-rail-h">
        Not yet placed · {weekPlan.pile.length}
        {weekPlan.pileTruncated ? '+' : ''}
      </div>
      {weekPlan.pile.length === 0 ? (
        <p className="muted">Nothing waiting. Every open task already has a day.</p>
      ) : (
        <ul className="week-chips">
          {weekPlan.pile.map((chip) => (
            <li key={chip.id}>
              <button
                type="button"
                className="week-chip"
                draggable={!stacked}
                data-testid="pile-chip"
                data-task-id={chip.id}
                onDragStart={(e) => startDraggingChip(e, chip)}
                onClick={() => onOpenTask(chip.id)}
              >
                {chip.title}
                {chip.estimateMinutes !== null && (
                  <span className="week-est"> · {spanLabel(chip.estimateMinutes)}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {weekPlan.pileTruncated && (
        <p className="muted">The fifty best are shown. The Tasks screen has the rest.</p>
      )}
    </aside>
  );

  /*
    The phone (§4 of the request).

    Seven columns do not fit 375px, so below `PANES_STACK` the canvas is a vertical list of seven
    day cards: the date, the day's room, and the chips already pointed at it. Placing at an hour
    is a tap through to Today for that date — the screen that already owns hour-precision, rather
    than a second drag surface built to be used with a thumb. Dragging is off here rather than
    merely unstyled: a `draggable` chip on a touch screen swallows the scroll gesture.
  */
  if (stacked) {
    return (
      <div className="week-screen" data-testid="week">
        {nav}
        {/*
          The days first and the pile after — the opposite of the desktop order, measured rather
          than assumed. At 375px with 24 chips the rail ran 1200px, so the seven cards began below
          a screen and a half of backlog: the thing you opened the week to see was the last thing
          on the page. The phone's gesture is "tap a day", so the days lead; the pile is a list you
          scroll to when you want it.
        */}
        <ol className="week-cards">
          {weekPlan.days.map((day) => (
            <li key={day.date}>
              <button
                type="button"
                className={dayClass('week-card', day, today)}
                data-testid="week-card"
                data-date={day.date}
                onClick={() => onOpenDay(day.date)}
              >
                <span className="week-card-head">
                  <b>{dayLabel(day.date)}</b>
                  <span className="week-free">{headerFigure(day, today)}</span>
                </span>
                {day.tasks.length === 0 && day.events.length === 0 ? (
                  <span className="muted">Nothing yet</span>
                ) : (
                  <span className="week-card-body">
                    {day.tasks.map((c) => c.title).join(' · ')}
                    {day.events.length > 0 && (
                      <span className="muted">
                        {day.tasks.length > 0 ? ' · ' : ''}
                        {day.events.length} on the calendar
                      </span>
                    )}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ol>
        <HabitsRow review={review.data} days={weekPlan.days} />
        {pile}
      </div>
    );
  }

  return (
    <div className="week-screen" data-testid="week">
      {nav}
      <div className="week-canvas">
        {pile}
        <div className="week-board">
          <div className="week-head">
            <span className="week-gutter" />
            {weekPlan.days.map((day) => (
              <div
                key={day.date}
                className={dayClass('week-col-head', day, today)}
                data-testid="week-col-head"
                data-date={day.date}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => dropOnColumn(e, day)}
              >
                <b>{dayLabel(day.date)}</b>
                <span
                  className={day.dated.minutes > day.budget.freeMinutes ? 'week-over' : 'week-free'}
                  data-testid="week-budget"
                >
                  {headerFigure(day, today)}
                </span>
              </div>
            ))}
          </div>

          <div className="week-grid" style={{ height: hours.length * WEEK_PX_PER_HOUR }}>
            <div className="week-gutter">
              {hours.map((hour) => (
                <span
                  key={hour}
                  className="week-hour-label"
                  style={{ top: (hour - window.startHour) * WEEK_PX_PER_HOUR }}
                >
                  {minuteLabel((hour % 24) * 60)}
                </span>
              ))}
            </div>

            {weekPlan.days.map((day) => {
              const dayStart = new Date(day.dayStart).getTime();
              const dated = day.tasks.filter((c) => c.plannedMinutes === 0);
              return (
                <div
                  key={day.date}
                  className={dayClass('week-col', day, today)}
                  data-testid="week-col"
                  data-date={day.date}
                  data-hours={day.hours}
                  data-start-hour={window.startHour}
                  data-px-per-hour={WEEK_PX_PER_HOUR}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const bounds = e.currentTarget.getBoundingClientRect();
                    setPreview({ date: day.date, minutes: minutesIn(day, e.clientY - bounds.top) });
                  }}
                  onDragLeave={(e) => {
                    // `dragleave` also fires on crossing into a child — an hour line, a block —
                    // so clearing unconditionally makes the preview flicker out mid-drag.
                    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                    setPreview(null);
                  }}
                  onDrop={(e) => dropOnGrid(e, day)}
                >
                  {hours.map((hour) => (
                    <i
                      key={hour}
                      className="week-hour"
                      style={{ top: (hour - window.startHour) * WEEK_PX_PER_HOUR }}
                    />
                  ))}

                  {/*
                    Dated but not placed, at the top of the column and dashed.

                    A separate look from a block on purpose: "Thursday" and "Thursday at two" are
                    different promises, and a screen that drew them the same would be claiming the
                    stronger one on the strength of the weaker.
                  */}
                  {dated.length > 0 && (
                    <div className="week-dated" data-testid="week-dated">
                      {dated.map((chip) => (
                        <button
                          type="button"
                          key={chip.id}
                          className={chip.status === 'done' ? 'week-chip is-done' : 'week-chip'}
                          data-testid="dated-chip"
                          data-task-id={chip.id}
                          draggable
                          onDragStart={(e) => startDraggingChip(e, chip)}
                          onClick={() => onOpenTask(chip.id)}
                        >
                          {chip.title}
                        </button>
                      ))}
                    </div>
                  )}

                  {day.events.map((event) => {
                    const from = (new Date(event.startsAt).getTime() - dayStart) / 60_000;
                    const to = (new Date(event.endsAt).getTime() - dayStart) / 60_000;
                    return (
                      <div
                        key={event.id}
                        className={event.taskId ? 'week-block task' : 'week-block cal'}
                        data-testid="week-block"
                        data-event-id={event.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('planner/event', event.id);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        style={{
                          top: ((from - window.startHour * 60) / 60) * WEEK_PX_PER_HOUR,
                          height: Math.max(14, ((to - from) / 60) * WEEK_PX_PER_HOUR),
                        }}
                        title={`${minuteLabel(Math.round(from))} ${event.title}`}
                      >
                        {event.title}
                      </div>
                    );
                  })}

                  {preview?.date === day.date && (
                    <div
                      className="week-preview"
                      data-testid="week-preview"
                      data-start-minutes={preview.minutes}
                      style={{
                        top: ((preview.minutes - window.startHour * 60) / 60) * WEEK_PX_PER_HOUR,
                        height: (DEFAULT_BLOCK_MINUTES / 60) * WEEK_PX_PER_HOUR,
                      }}
                    >
                      {minuteLabel(preview.minutes)}
                    </div>
                  )}
                </div>
              );
            })}

            {/*
              The measured ordinary case, said once.

              `pointer-events: none` so it is a caption and not a drop target — the same mistake
              `Today.tsx`'s now-line comment names, where an overlay swallowed the drop it was
              describing.
            */}
            {!anythingPlanned && (
              <p className="week-empty" data-testid="week-empty">
                Nothing planned this week. Drag from the left onto a day to date it, or onto an hour
                to place it.
              </p>
            )}
          </div>

          <HabitsRow review={review.data} days={weekPlan.days} />
        </div>
      </div>
    </div>
  );
}

/** Half an hour, the length a drop means when nobody estimated — `Today.tsx`'s click default. */
const DEFAULT_BLOCK_MINUTES = 30;

/**
 * How tall an hour is here: half of `WEEK_PX_PER_HOUR`.
 *
 * The day timeline gets 56px because it is one column and reading a 15-minute block matters. Seven
 * columns of eighteen hours at 56px is a 1008px canvas nobody can see a week in, which defeats the
 * point of the screen. Published on each column as `data-px-per-hour`, the way `Today.tsx`
 * publishes its own, so the end-to-end specs compute drop coordinates from the number actually
 * drawn rather than restating it — the mistake `timelineLayout.ts`'s own note records.
 */
const WEEK_PX_PER_HOUR = 28;

const chipFor = (plan: WeekPlan | undefined, taskId: string): WeekChip | undefined =>
  plan && [...plan.pile, ...plan.days.flatMap((d) => d.tasks)].find((c) => c.id === taskId);

/**
 * Past dims, today is tinted, the rest is plain.
 *
 * Past columns dim but stay readable rather than being hidden or greyed out of legibility: the
 * week is a record as much as a plan, which is the decision the timeline already made about
 * completed blocks.
 */
const dayClass = (base: string, day: WeekDay, today: string): string =>
  [base, day.date < today ? 'is-past' : '', day.date === today ? 'is-today' : '']
    .filter(Boolean)
    .join(' ');

/**
 * What the column header says under the date.
 *
 * A past day reports what is still sitting on it unplaced, because free hours on a day that has
 * gone are not room and saying "5h free" about last Tuesday would be nonsense. A future or
 * current day reports its room, and — when something is dated to it — how much is pointed at it.
 * Rows nobody estimated are counted out loud rather than folded into a minute figure that would
 * then be a confident understatement; on every persona account measured, most rows carry no
 * estimate.
 */
function headerFigure(day: WeekDay, today: string): string {
  const unmeasured = day.dated.unestimated > 0 ? ` · ${day.dated.unestimated} unestimated` : '';
  if (day.date < today) {
    return day.dated.count === 0 ? '—' : `${day.dated.count} left${unmeasured}`;
  }
  if (day.dated.minutes === 0 && day.dated.unestimated === 0) {
    return day.budget.freeMinutes === 0 ? '—' : `${spanLabel(day.budget.freeMinutes)} free`;
  }
  const over = day.dated.minutes - day.budget.freeMinutes;
  return over > 0
    ? `${spanLabel(over)} over${unmeasured}`
    : `${spanLabel(day.dated.minutes)} on ${spanLabel(day.budget.freeMinutes)} free${unmeasured}`;
}

/**
 * "Mon 7" from a plan date.
 *
 * No zone argument, and no `Intl` — deliberately, twice over. A `scheduledDate` is already the
 * account's calendar day, a `YYYY-MM-DD` string with no instant behind it, so there is nothing
 * left for a zone to decide; taking one would be a parameter that could only ever be ignored or
 * wrong. And this is called once per column header plus once per card, where a fresh
 * `Intl.DateTimeFormat` costs ~81µs against 1.3µs for a reused one (measured in Phase 86). Noon
 * anchors the parse far from either midnight, the same guard `shiftDay` uses.
 */
function dayLabel(date: string): string {
  const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const at = new Date(`${date}T12:00:00Z`);
  return `${names[(at.getUTCDay() + 6) % 7]} ${at.getUTCDate()}`;
}

/**
 * The habits row beneath the canvas: how the week is going, per day and in total.
 *
 * From `getWeekReview`, which already answers this and answers it once — the row and the
 * Reflection screen are then the same figures rather than two reads that can disagree.
 *
 * ## Why one band of dots and not one band per habit
 *
 * The mockup drew a dotted row per habit. `getWeekReview` cannot fill that: `byDay` carries
 * `habitsMet` and `habitsLogged` for the *day*, across every habit, and `habits[]` carries each
 * habit's total for the *week*. There is no per-habit-per-day figure in the payload, so seven
 * rows of dots would have been the same seven values repeated under different names — a screen
 * making a per-habit claim out of an account-wide number.
 *
 * So the dots are the account's week, one per day, and each habit gets its own total beside its
 * name. Both halves are then things the payload actually says. A genuine per-habit strip is a
 * `get_habit_history` read per habit and is its own piece of work, not something to fake here.
 *
 * Rendered as nothing at all when the account keeps no habits. That is the manager persona's
 * case, measured — they have none — and a labelled band of seven blank cells says less than an
 * absent band while taking more of the screen.
 */
function HabitsRow({ review, days }: { review: WeekReview | undefined; days: WeekDay[] }) {
  if (!review || review.habits.length === 0) return null;

  const byDay = new Map(review.byDay.map((d) => [d.day, d]));
  const asOf = reviewAsOf(review);
  return (
    <div className="week-habits" data-testid="week-habits">
      <div className="week-habit">
        <span className="week-habit-name">Habits</span>
        {days.map((day) => {
          const met = byDay.get(day.date)?.habitsMet ?? 0;
          const logged = byDay.get(day.date)?.habitsLogged ?? 0;
          return (
            <span
              key={day.date}
              className={met > 0 ? 'week-dot is-on' : 'week-dot'}
              title={`${dayLabel(day.date)}: ${met} met of ${logged} logged`}
            />
          );
        })}
        <span className="week-habit-total">
          {review.habitsMet} / {review.habits.length} met
        </span>
      </div>
      <ul className="week-habit-totals">
        {review.habits.map((habit) => (
          <li key={habit.id}>
            {habit.name}{' '}
            {/* The one figure, over the window the review measured it in — see `habitFigure`.
                It was `{actual} / {target}`: no unit, no `amount()` (so *17.900000000000002 / 18*),
                and *Body fat 0 / 14* for a reading nobody took that week (`01a03813-b97d`). */}
            <span className="muted">
              {habitFigure(habit, { window: habit.period, asOf, today: review.today })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
